import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function pesoLegible(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Elegir el libro de Excel de la carga inicial.
 *
 * Sustituye al `<input type="file">` de fábrica, que es lo único de todo el
 * portal que se veía como una página de 1998 —un botón gris que dice «Choose
 * File» en inglés, en una pantalla en español— y que en una laptop chica
 * empujaba el nombre del archivo fuera de la caja.
 *
 * **El input sigue existiendo debajo.** No está de adorno: arrastrar no es una
 * opción con teclado ni con lector de pantalla, así que se oculta a la vista y
 * no a la accesibilidad, igual que en la carga de versiones.
 */
export function SelectorDeLibro({
  archivo,
  ocupado,
  onElegir,
}: {
  archivo: File | null;
  ocupado: boolean;
  onElegir: (archivo: File | null) => void;
}) {
  const { t } = useTranslation();
  const entrada = useRef<HTMLInputElement>(null);
  const [encima, setEncima] = useState(false);
  const [rechazo, setRechazo] = useState<string | null>(null);
  // `dragleave` salta también al pasar sobre un hijo: contar entradas y salidas
  // evita que el recuadro parpadee mientras el cursor sigue dentro.
  const profundidad = useRef(0);

  function aceptar(elegido: File | undefined) {
    if (!elegido) return;
    if (!elegido.name.toLowerCase().endsWith('.xlsx')) {
      setRechazo(t('initialLoad.notAWorkbook'));
      return;
    }
    setRechazo(null);
    onElegir(elegido);
  }

  if (archivo) {
    return (
      <div className="libro-elegido">
        <span className="libro-elegido__icono" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 2.5h8l4 4v11H4z" strokeLinejoin="round" />
            <path d="M12 2.5v4h4" strokeLinejoin="round" />
            <path d="m7.5 10 5 5m0-5-5 5" strokeLinecap="round" />
          </svg>
        </span>
        <span className="libro-elegido__datos">
          <span className="libro-elegido__nombre">{archivo.name}</span>
          <span className="libro-elegido__peso">{pesoLegible(archivo.size)}</span>
        </span>
        <button
          type="button"
          className="boton boton--fantasma"
          disabled={ocupado}
          onClick={() => {
            onElegir(null);
            if (entrada.current) entrada.current.value = '';
          }}
        >
          {t('initialLoad.change')}
        </button>
        <input
          ref={entrada}
          id="libro"
          className="visualmente-oculto"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => aceptar(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={`zona-libro${encima ? ' zona-libro--encima' : ''}`}
        onDragEnter={(e) => {
          e.preventDefault();
          profundidad.current += 1;
          setEncima(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => {
          profundidad.current -= 1;
          if (profundidad.current <= 0) setEncima(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          profundidad.current = 0;
          setEncima(false);
          aceptar(e.dataTransfer.files?.[0]);
        }}
      >
        <svg
          className="zona-libro__icono"
          viewBox="0 0 24 24"
          width="26"
          height="26"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M12 15.5V4m0 0L8 8m4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" strokeLinecap="round" />
        </svg>
        <p className="zona-libro__texto">
          {t('initialLoad.dropHere')}{' '}
          <label className="zona-libro__enlace" htmlFor="libro">
            {t('initialLoad.orChoose')}
          </label>
        </p>
        <p className="zona-libro__nota">{t('initialLoad.fileHelp')}</p>
        <input
          ref={entrada}
          id="libro"
          className="visualmente-oculto"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => aceptar(e.target.files?.[0])}
        />
      </div>
      {rechazo && (
        <p className="aviso aviso--atencion" role="alert">
          {rechazo}
        </p>
      )}
    </>
  );
}
